<script setup lang="ts" generic="T extends object = Record<string, number>">
import type { ChartContext } from '#ui-kit/charts/lib/context'
import { formatValue } from '@forgd/formatter'
import { StackedBar } from '@unovis/ts'
import { VisTooltip } from '@unovis/vue'
import { computed, getCurrentInstance, inject, useAttrs } from 'vue'
import { chartContextKey } from '#ui-kit/charts/lib/context'
import { renderTooltip, tooltipAttributes } from '#ui-kit/charts/lib/render-tooltip'

const props = withDefaults(defineProps<ChartOverlayTriggerProps>(), {
  xAxis: 'month',
  selector: StackedBar.selectors.bar,
})

const emit = defineEmits<ChartOverlayTriggerEmitProps<T>>()

// slots
const slots = defineSlots<{
  default: (attrs: Omit<ChartOverlayTriggerData<T>, 'x'> & { x: number }) => void
}>()

const defaultSlot = slots.default
const attrs = useAttrs()
const instance = getCurrentInstance()

// inject store (for meta injection, and to update hovered values)
const store = inject(chartContextKey, undefined)

// meta
const meta = computed<ChartContext['meta'] | undefined>(() => {
  return props.meta ? props.meta : store ? store.meta : undefined
})

// cache unovis tooltip template values
let prevData: T
let prevTemplate = ''

// tooltip
const container = document.createElement('div')
const tooltip = document.createElement('div')
tooltip.className = ` py-2 px-2.5 rounded
                      flex-col justify-start items-start gap-2 inline-flex
                      bg-primary-900 text-white
                      text-xs leading-normal
                      shadow-lg`
container.appendChild(tooltip)

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

// computed properties
const tooltipOptions = computed(() => {
  // input
  const { meta: _, selector, ...rest } = props

  // variables
  const xAxis = (meta.value ? meta.value.xAxis : undefined) || props.xAxis
  const xAxisKey = String(xAxis)
  const xLabel = (meta.value ? meta.value.labels?.x : undefined) || formatValue(xAxisKey, 'string:title')

  // template - normalise the trigger argument shape
  // some selectors (e.g. StackedBar) pass { datum: T }, others pass T directly
  const template = (raw: unknown) => {
    const outer = isRecord(raw) ? raw : {}
    const { datum } = outer
    const record = isRecord(datum) ? datum : outer
    const d = record as T

    // determine index (use xAxis if available, otherwise try to get from data)
    const { index: recordIndex = 0 } = record
    const indexValue = xAxisKey in record ? record[xAxisKey] : recordIndex
    const numericIndex = Number(indexValue ?? 0)
    const index = Number.isFinite(numericIndex) ? numericIndex : 0

    // re-render only if the data changes
    if (d !== prevData) {
      // update cached reference
      prevData = d

      // data (split x and y values)
      const { [xAxisKey]: x = undefined, ...y } = record
      const payload: ChartOverlayTriggerData<T> = {
        index: Math.abs(Math.round(index)),
        d,
        x: typeof x === 'number' ? x : undefined,
        y: y as T, // see comment in ChartOverlayTriggerData
      }

      // hovered
      if (store) {
        store.data.onHover({ y })
      }

      // events
      emit('hover', payload)

      // render
      if (defaultSlot) {
        // render
        // the slot types x as a number; it is undefined when the x-axis key is missing from the data
        renderTooltip(instance, tooltip, () =>
          defaultSlot(payload as Omit<ChartOverlayTriggerData<T>, 'x'> & { x: number }),
        )
      } else {
        tooltip.innerHTML = `${xLabel} ${x}`
      }
      prevTemplate = container.innerHTML
    }

    // otherwise return cached template
    return prevTemplate
  }

  // return with triggers
  return {
    attributes: tooltipAttributes(attrs.class),
    ...rest,
    triggers: {
      [selector ?? StackedBar.selectors.bar]: template,
    },
  }
})
</script>

<script lang="ts">
export interface ChartOverlayTriggerProps {
  xAxis?: string
  meta?: ChartContext['meta']
  // Selector for the chart element to trigger tooltip (e.g., StackedBar.selectors.bar)
  selector?: string
}

/**
 * Data structure for overlay and hover events
 */
export interface ChartOverlayTriggerData<T> {
  // The index of the hovered data point
  index: number

  // The x-axis value (if x-axis prop or provided)
  // This will be undefined if xAxis key doesn't exist in the data
  x: number | undefined

  // The y-axis values (if x-axis prop or provided)
  // At runtime, this will exclude the xAxis property, but for type simplicity
  // (issues with props vs inject) we maintain the full type T
  y: T

  // The complete data object
  d: T
}

export interface ChartOverlayTriggerEmitProps<T> {
  (e: 'hover', data: ChartOverlayTriggerData<T>): void
}
</script>

<template>
  <VisTooltip v-bind="tooltipOptions" />
</template>

<style>
/* required as adding classes to tooltip disables the transition */
.unovis-xy-container {
  div[data-name='tooltip'] {
    z-index: var(--z-index-popover);
    pointer-events: none;
    transition: opacity 0.5s;
    opacity: 0;
  }

  &:hover div[data-name='tooltip'] {
    transition: opacity 0.5s;
    opacity: 0.9;
  }
}
</style>
